import store from "@/store/store.js";
import maplibregl from "maplibre-gl";
import { polygon, point } from "@turf/helpers";
import bbox from "@turf/bbox";
import bboxPolygon from "@turf/bbox-polygon";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import booleanIntersects from "@turf/boolean-intersects";
import MapConstants from "@/constants/mapConstants";
import { toCircle } from "@terraformer/spatial";
import center from "@turf/center";

export default class MapHelpers {
  static fitBounds(coordOrCoordArr, options) {
    let map;
    if (options?.map) {
      map = options.map;
    } else {
      map = store.getters["MAP/map"];
    }
    if (!map) {
      console.warn("Cannot call fitBounds - map is not initialized yet");
      return;
    }

    const coordArr = this.handleCoordinates(coordOrCoordArr);
    if (!coordOrCoordArr || !Array.isArray(coordOrCoordArr) || !coordOrCoordArr.length) {
      console.warn("fitBounds failed - invalid coordinate or coordinate array provided");
      return;
    }
    let bounds;
    try {
      bounds = coordArr.reduce(function (allBounds, coord) {
        try {
          return allBounds.extend(coord);
        } catch (e) {
          console.debug(e.message, coord);
        }
      }, new maplibregl.LngLatBounds(coordArr[0], coordArr[0]));
    } catch (e) {
      console.debug(e.message, coordArr);
    }

    try {
      // stop method + setTimeout(0) is used to stop running animation.
      // Otherwise when fitBounds is called again before previous animation ends, both old and new animations halt
      // TODO: check this part again if animation interruption happens
      try {
        map.stop();
      } catch (stopError) {
        // Ignore "already stopped" errors
        console.debug("Map stop error (ignoring):", stopError);
      }

      setTimeout(() => {
        try {
          map.setPadding({ top: 0, bottom: 0, left: 0, right: 0 });
        } catch (paddingError) {
          console.debug("Map setPadding error (ignoring):", paddingError);
        }

        if (bounds) {
          let defaultPadding = window?.screen?.width && window.screen.width < 1280 ? 100 : 250;

          // Safely get current bearing with error handling
          let currentBearing = 0;
          try {
            if (map && typeof map.getBearing === "function") {
              currentBearing = map.getBearing();
            }
          } catch (e) {
            console.debug("Could not get current bearing, using default (0):", e);
            currentBearing = 0;
          }

          const fitBoundsOptions = {
            padding: options?.padding || defaultPadding,
            duration: 500,
            maxZoom: options?.maxZoom || 24
          };

          // Only add bearing if explicitly requested to preserve it
          if (options?.preserveBearing === true) {
            fitBoundsOptions.bearing = currentBearing;
          }

          map.fitBounds(bounds, fitBoundsOptions);
        }
      }, 500);
    } catch (e) {
      console.debug(e.message, bounds);
    }
  }

  static fitBoundsIfNotOnTheScreen(coordOrCoordArr, options) {
    const map = store.getters["MAP/map"];
    if (!map) {
      console.warn("Cannot call fitBounds - map is not initialized yet");
      return;
    }

    // Get current viewport coordinates
    const viewportCoords = this.getViewportCoordinates();
    if (!viewportCoords || !viewportCoords.length) {
      // If we can't get viewport coords, fallback to regular fitBounds
      this.fitBounds(coordOrCoordArr, options);
      return;
    }

    // Create a feature from the viewport coordinates
    const viewportPolygon = polygon(viewportCoords);

    // Process input coordinates
    const coordArr = this.handleCoordinates(coordOrCoordArr);
    if (!coordOrCoordArr || !Array.isArray(coordOrCoordArr) || !coordOrCoordArr.length) {
      console.warn("fitBoundsIfNotOnTheScreen failed - invalid coordinate or coordinate array provided");
      return;
    }

    // Convert coordinates to a feature
    const targetFeature = this.coordToFeature(coordArr);
    if (!targetFeature) {
      // If we couldn't create a feature, fall back to regular fitBounds
      console.debug("Could not create target feature, falling back to regular fitBounds");
      this.fitBounds(coordOrCoordArr, options);
      return;
    }

    // Check if the target coordinates are visible in the current viewport
    let isVisible = false;
    try {
      // For point features
      if (targetFeature.geometry.type === "Point") {
        // Check if point is within viewport polygon
        isVisible = booleanPointInPolygon(targetFeature, viewportPolygon);
      } else {
        // For polygons or other geometries, check for any intersection
        isVisible = booleanIntersects(targetFeature, viewportPolygon);
      }
    } catch (e) {
      console.debug("Error checking visibility:", e);
      // If there's an error checking visibility, fall back to regular fitBounds
      this.fitBounds(coordOrCoordArr, options);
      return;
    }

    // Only fit bounds if the feature is not visible
    if (!isVisible && map) {
      this.fitBounds(coordOrCoordArr, options);
    }
  }

  static fitBoundsMultipleFeatures(features) {
    if (features?.length > 0) {
      const featureCollection = { type: "FeatureCollection", features: features };
      const boundingBox = bbox(featureCollection);
      const boundingBoxPolygon = bboxPolygon(boundingBox);
      this.fitBounds(boundingBoxPolygon.geometry?.coordinates?.[0], {
        padding: {
          top: 100,
          bottom: 100,
          left: 100,
          right: 420 // Left panel width is 320px
        }
      });
    }
  }

  static jumpToPoint(coordinate, options) {
    const map = store.getters["MAP/map"];
    if (!map) {
      console.warn("Cannot call fitBounds - map is not initialized yet");
      return;
    }
    map.jumpTo({ center: coordinate, zoom: options?.zoom || 19, padding: options?.padding || 250 });
  }

  static centerOnFeature(coordinates, options = {}) {
    const map = store.getters["MAP/map"];
    if (!map) {
      console.warn("Cannot call centerOnFeature - map is not initialized yet");
      return;
    }

    // Get current zoom level to preserve it
    const currentZoom = map.getZoom();

    // Handle different geometry types
    let centerPoint;

    if (Array.isArray(coordinates) && coordinates.length === 2 && typeof coordinates[0] === "number") {
      // Point geometry: [lng, lat]
      centerPoint = coordinates;
    } else if (Array.isArray(coordinates) && Array.isArray(coordinates[0])) {
      // Polygon geometry: calculate centroid
      try {
        const coordArr = this.handleCoordinates(coordinates);
        const bounds = coordArr.reduce(function (allBounds, coord) {
          return allBounds.extend(coord);
        }, new maplibregl.LngLatBounds(coordArr[0], coordArr[0]));

        centerPoint = bounds.getCenter().toArray();
      } catch (e) {
        console.debug("Error calculating polygon center:", e);
        return;
      }
    } else {
      console.warn("Invalid coordinates provided to centerOnFeature:", coordinates);
      return;
    }

    map.stop();
    setTimeout(() => {
      map.setPadding({ top: 0, bottom: 0, left: 0, right: 0 });
      map.flyTo({
        center: centerPoint,
        zoom: currentZoom, // Preserve current zoom level
        duration: options?.duration || 500,
        padding: options?.padding || { top: 0, bottom: 0, left: 0, right: 0 }
      });
    }, 0);
  }

  static fitAroundPoint(coordinate, options = {}) {
    options.maxZoom = options?.maxZoom || 19;
    const boundingCircle = toCircle(coordinate, 1, 4);
    this.fitBounds(boundingCircle?.geometry?.coordinates?.[0], options);
  }

  static fitAroundPointIfNotOnTheScreen(coordinate, options = {}) {
    const map = store.getters["MAP/map"];
    if (!map) {
      console.warn("Cannot call fitAroundPointIfNotOnTheScreen - map is not initialized yet");
      return;
    }

    // Get current viewport coordinates
    const viewportCoords = this.getViewportCoordinates();
    if (!viewportCoords || !viewportCoords.length) {
      // If we can't get viewport coords, fallback to regular fitAroundPoint
      this.fitAroundPoint(coordinate, options);
      return;
    }

    // Create a feature from the viewport coordinates
    const viewportPolygon = polygon(viewportCoords);

    // Create a point feature from the coordinate
    const pointFeature = point(coordinate);

    // Check if the point is visible in the current viewport
    let isVisible = false;
    try {
      isVisible = booleanPointInPolygon(pointFeature, viewportPolygon);
    } catch (e) {
      console.debug("Error checking point visibility:", e);
      // If there's an error checking visibility, fall back to regular fitAroundPoint
      this.fitAroundPoint(coordinate, options);
      return;
    }

    // Only fit around point if it's not visible
    if (!isVisible) {
      options.maxZoom = options?.maxZoom || 19;
      const boundingCircle = toCircle(coordinate, 1, 4);
      this.fitBounds(boundingCircle?.geometry?.coordinates?.[0], options);
    }
  }

  static panMapBy(x = 0, y = 0) {
    const map = store.getters["MAP/map"];

    setTimeout(() => {
      map.stop();
      map.panBy([x, y], { duration: 99 });
      setTimeout(() => {
        map.setPitch(0);
      }, 100);
    }, 0);
    // old one remove if unnecessary.
    // map.once("idle", () => {
    //   setTimeout(() => {
    //     map.panBy([x, y], { duration: 100 });
    //     setTimeout(() => {
    //       map.resetNorthPitch({
    //         animate: true,
    //         duration: 100,
    //         easing(x) {
    //           return x;
    //         }
    //       });
    //     }, 101);
    //   }, 0);
    // });
  }

  /**
   * Convert different type of coordinates to supported style,
   * example: [[1.23]] => [[1.23, 1.23]]
   */
  static handleCoordinates(coordOrCoordArr) {
    if (!coordOrCoordArr || !Array.isArray(coordOrCoordArr) || !coordOrCoordArr.length) {
      return coordOrCoordArr;
    }
    const isCoordArr = Array.isArray(coordOrCoordArr[0]);
    let coordArr = isCoordArr ? coordOrCoordArr : [coordOrCoordArr];

    return coordArr.map((coord) => {
      if (coord.length === 1) {
        const validCoord = [coord[0], coord[0]];
        console.warn("Invalid coordinta - single point of coordinate: ", coord, "defaults to: ", validCoord);
        coord = validCoord;
      }
      return coord.map((num) => {
        return typeof num === "string" ? Number.parseFloat(num) : num;
      });
    });
  }

  static toggleAllLayers(typeCodesObj) {
    for (const [typeCode, typeCodeObj] of Object.entries(typeCodesObj)) {
      if (typeCode) {
        this.toggleLayer(typeCode, typeCodeObj.isVisible);
      }
    }
  }

  static toggleLayer(typeCode, isVisible) {
    const map = store.getters["MAP/map"];
    const fillLayerId = `fill_${typeCode}_ptr`;
    const symbolLayerId = `symbol_${typeCode}_ptr`;
    if (map.getLayer(fillLayerId)) {
      map.setLayoutProperty(fillLayerId, "visibility", isVisible ? "visible" : "none");
    }
    if (map.getLayer(symbolLayerId)) {
      map.setLayoutProperty(symbolLayerId, "visibility", isVisible ? "visible" : "none");
    }
  }

  static coordToFeature(coordArr) {
    try {
      if (!coordArr || !Array.isArray(coordArr)) {
        console.debug("Invalid coordinates provided to coordToFeature:", coordArr);
        return null;
      }

      // Handle single point coordinate [lng, lat]
      if (typeof coordArr[0] === "number" && typeof coordArr[1] === "number") {
        return point(coordArr);
      }
      // Handle polygon coordinates [[[lng, lat], [lng, lat], ...]]
      else if (Array.isArray(coordArr[0]) && coordArr[0].length) {
        // For simple polygon without holes
        if (Array.isArray(coordArr[0][0])) {
          return polygon([coordArr[0]]);
        }
        // For standard polygon coordinates [[lng, lat], [lng, lat], ...]
        else {
          return polygon([coordArr]);
        }
      }
      console.debug("Unrecognized coordinate format:", coordArr);
      return null;
    } catch (error) {
      console.debug("Failed to convert coord to feature - ", error);
      console.debug("Coordinate: ", coordArr);
      return null;
    }
  }

  static getViewportCoordinates() {
    try {
      const map = store.getters["MAP/map"];
      const canvas = map.getCanvas();
      const w = canvas.width;
      const h = canvas.height;
      const cUL = map.unproject([0, 0]).toArray();
      const cUR = map.unproject([w, 0]).toArray();
      const cLR = map.unproject([w, h]).toArray();
      const cLL = map.unproject([0, h]).toArray();
      const coordinates = [cUL, cUR, cLR, cLL, cUL];
      return [coordinates];
    } catch (err) {
      console.debug("Failed to check if boxes intersect - ", err);
    }
    return [];
  }

  static eyeIconClicked(list, contentName) {
    const typeCode = list.features[0].properties.typeCode;
    const isVisible = store.getters["CONTENT/getTypeCodeObjVisibility"](typeCode, contentName);
    if (isVisible === undefined) {
      return;
    }
    store.commit("CONTENT/TOGGLE_TYPE_CODE_VISIBILITY", {
      typeCode: typeCode,
      contentName: contentName,
      isVisible: !isVisible
    });
    list.isVisible = !isVisible;
    store.dispatch("CONTENT/SET_VISIBILITY_OF_TYPE_CODE", { contentName, isVisible: list.isVisible, typeCode });
    this.toggleLayer(typeCode, list.isVisible);
  }

  static filterList(filterText, data, isTreeStructure = false) {
    //TODO: Highlight on filter. Currently it highlights all features on refresh data
    // store.dispatch("MAP/UNHIGHLIGHT_ALL_FEATURES");

    // Always dispatch to store to ensure filter state is synchronized
    // This is important when clearing the filter (empty string) so that
    // the store's filterText gets cleared and filtered arrays are reset
    if (filterText !== undefined && filterText !== null) {
      store.dispatch("CONTENT/FILTER_TEXT", filterText);
    }

    if (filterText === undefined || filterText === null || filterText === "") {
      return isTreeStructure ? data : undefined;
    } else {
      // For very large datasets, use Web Worker to prevent main thread blocking
      if (isTreeStructure && data && data.length > 5000) {
        return this.filterTreeStructureWithWorker(filterText, data);
      }

      return isTreeStructure ? this.filterTreeStructureOptimized(filterText, data) : undefined;
    }
  }

  // Web Worker-based filtering for large datasets
  static filterTreeStructureWithWorker(filterText, treeData) {
    return new Promise((resolve) => {
      // Check if Web Workers are supported
      if (typeof Worker === "undefined") {
        console.warn("Web Workers not supported, falling back to main thread filtering");
        resolve(this.filterTreeStructureOptimized(filterText, treeData));
        return;
      }

      try {
        const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
        const workerUrl = baseUrl + "/src/workers/treeFilterWorker.js";

        const worker = new Worker(workerUrl, { type: "module" });

        worker.postMessage({
          type: "FILTER_TREE",
          data: {
            filterText,
            treeData
          }
        });

        worker.onmessage = (e) => {
          if (e.data.type === "FILTER_COMPLETE") {
            worker.terminate();
            resolve(e.data.result);
          }
        };

        worker.onerror = (error) => {
          console.error("Worker error:", error);
          worker.terminate();
          // Fallback to main thread filtering
          resolve(this.filterTreeStructureOptimized(filterText, treeData));
        };

        // Timeout after 10 seconds
        setTimeout(() => {
          worker.terminate();
          console.warn("Worker timeout, falling back to main thread filtering");
          resolve(this.filterTreeStructureOptimized(filterText, treeData));
        }, 10000);
      } catch (error) {
        console.error("Failed to create worker:", error);
        resolve(this.filterTreeStructureOptimized(filterText, treeData));
      }
    });
  }

  static filterMapObjects(mapObjects, searchTerm) {
    return mapObjects.filter(
      (mapObject) =>
        mapObject.name?.toLowerCase().includes(searchTerm) ||
        mapObject.id?.toString().toLowerCase().includes(searchTerm)
    );
  }

  static doesTypeCodeMatch(typeCode, searchTerm) {
    const typeCodeMatches = typeCode.name?.toLowerCase().includes(searchTerm);
    const taxonomy = store.state.taxonomy;
    const typeCodeDescription = taxonomy[typeCode.name]?.title;
    const typeCodeDescriptionMatches = typeCodeDescription?.toLowerCase().includes(searchTerm);
    return typeCodeMatches || typeCodeDescriptionMatches;
  }

  static processTypeCode(typeCode, searchTerm) {
    const filteredTypeCode = {
      ...typeCode,
      children: undefined
    };

    if (!typeCode.children || typeCode.children.length === 0) {
      return null;
    }

    if (this.doesTypeCodeMatch(typeCode, searchTerm)) {
      filteredTypeCode.children = typeCode.children;
      filteredTypeCode.childrenCount = typeCode.children.length;
      return filteredTypeCode;
    }

    const filteredMapObjects = this.filterMapObjects(typeCode.children, searchTerm);
    if (filteredMapObjects.length > 0) {
      filteredTypeCode.children = filteredMapObjects;
      filteredTypeCode.childrenCount = filteredMapObjects.length;
      return filteredTypeCode;
    }

    return null;
  }

  static calculateChildrenCount(children) {
    return children?.reduce((sum, child) => sum + (child.childrenCount || 0), 0) || 0;
  }

  static processLevel(level, searchTerm) {
    const filteredLevel = {
      ...level,
      children: undefined
    };

    if (!level.children || level.children.length === 0) {
      return null;
    }

    const filteredTypeCodes = level.children
      .map((typeCode) => this.processTypeCode(typeCode, searchTerm))
      .filter(Boolean);

    const levelNameMatches = level.name?.toLowerCase().includes(searchTerm);
    if (levelNameMatches) {
      filteredLevel.children = level.children;
      filteredLevel.childrenCount = this.calculateChildrenCount(level.children);
      return filteredLevel;
    }

    if (filteredTypeCodes.length > 0) {
      filteredLevel.children = filteredTypeCodes;
      filteredLevel.childrenCount = this.calculateChildrenCount(filteredTypeCodes);
      return filteredLevel;
    }

    return null;
  }

  static processBuilding(building, searchTerm) {
    const filteredBuilding = {
      ...building,
      children: undefined
    };

    if (!building.children || building.children.length === 0) {
      return null;
    }

    const filteredLevels = building.children.map((level) => this.processLevel(level, searchTerm)).filter(Boolean);

    const buildingNameMatches = building.name?.toLowerCase().includes(searchTerm);
    if (buildingNameMatches) {
      filteredBuilding.children = building.children;
      filteredBuilding.childrenCount = this.calculateChildrenCount(building.children);
      return filteredBuilding;
    }

    if (filteredLevels.length > 0) {
      filteredBuilding.children = filteredLevels;
      filteredBuilding.childrenCount = this.calculateChildrenCount(filteredLevels);
      return filteredBuilding;
    }

    return null;
  }

  static filterTreeStructureOptimized(filterText, treeData) {
    if (!filterText.trim() || !treeData) {
      return treeData;
    }

    const searchTerm = filterText.toLowerCase().trim();

    // Safety check for extremely large datasets
    if (treeData.length > 1000) {
      console.warn("Tree data too large for filtering, returning limited results to prevent crashes");
      // Return only first 1000 items to prevent crashes
      return treeData.slice(0, 1000);
    }

    // Enhanced search logic:
    // 1. If building name matches -> show ALL levels and typeCodes
    // 2. If level name matches -> show ALL typeCodes under that level
    // 3. If typeCode name/description matches -> show ALL map objects under that typeCode
    // 4. Otherwise -> filter map objects by name/ID

    try {
      return treeData.map((building) => this.processBuilding(building, searchTerm)).filter(Boolean);
    } catch (error) {
      console.error("Error in filterTreeStructureOptimized:", error);
      // Return original data on error to prevent crashes
      return treeData;
    }
  }

  // Legacy method for backward compatibility
  static filterTreeStructure(filterText, treeData) {
    return this.filterTreeStructureOptimized(filterText, treeData);
  }

  // Chunked processing for extremely large datasets (optional use)
  static async filterTreeStructureChunked(filterText, treeData, chunkSize = 100) {
    if (!filterText.trim() || !treeData) {
      return treeData;
    }

    try {
      // Process in chunks to avoid blocking the main thread
      const results = [];

      for (let i = 0; i < treeData.length; i += chunkSize) {
        const chunk = treeData.slice(i, i + chunkSize);

        // Process chunk
        const filteredChunk = this.filterTreeStructureOptimized(filterText, chunk);
        results.push(...filteredChunk);

        // Yield control to prevent blocking
        if (i + chunkSize < treeData.length) {
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
      }

      return results;
    } catch (error) {
      console.error("Error in filterTreeStructureChunked:", error);
      return treeData;
    }
  }

  static shouldForceUpdate(siteId, currentSiteData) {
    return siteId !== currentSiteData?.siteId || store.state.CONTENT.isAnyLevelUpdated;
  }

  static setBuildingWhenUndefined(buildingId, currentBuilding, currentSiteData) {
    if (buildingId !== undefined) {
      store.dispatch("MAP/SET_CURRENT_BUILDING", { building: buildingId });
    } else if (currentBuilding !== undefined) {
      store.dispatch("MAP/SET_CURRENT_BUILDING", { building: currentBuilding });
    } else {
      store.dispatch("MAP/SET_CURRENT_BUILDING", { building: currentSiteData?.buildings?.[0]?.properties?.fid });
    }
  }

  static setLevelWhenUndefined(currentLevel, currentSiteData) {
    let levelToSet = currentLevel;
    const currentBuildingObject = store.getters["MAP/currentBuildingObject"];
    const levels = currentSiteData?.levels?.filter(
      (level) => level.properties.bid === currentBuildingObject?.properties?.fid
    );
    const level = levels?.[0];
    const defaultLevel = levels?.find((level) => level?.properties?.isDefault);

    if (levelToSet === undefined && defaultLevel) {
      levelToSet = defaultLevel?.properties?.lvl;
      store.dispatch("MAP/SET_CURRENT_LEVEL", { level: levelToSet });
    } else if (level) {
      levelToSet = level.properties.lvl;
      store.dispatch("MAP/SET_CURRENT_LEVEL", { level: levelToSet });
    }
  }

  static moveMapToBuilding(shouldMoveMap) {
    if (!shouldMoveMap) {
      return;
    }

    const map = store.getters["MAP/map"];
    if (!map) {
      return;
    }

    const currentBuildingObject = store.getters["MAP/currentBuildingObject"];
    let fitBoundsCalled = false;

    map.once("idle", () => {
      fitBoundsCalled = true;
      MapHelpers.fitBounds(currentBuildingObject?.geometry?.coordinates?.[0]);
    });

    setTimeout(() => {
      if (!fitBoundsCalled) {
        MapHelpers.fitBounds(currentBuildingObject?.geometry?.coordinates?.[0]);
      } else if (map.getZoom() < 17 && fitBoundsCalled) {
        MapHelpers.fitBounds(currentBuildingObject?.geometry?.coordinates?.[0]);
      }
    }, 500);
  }

  static handleBuildingAndLevelWhenUndefined(
    buildingId,
    currentBuilding,
    currentLevel,
    currentSiteData,
    shouldMoveMap
  ) {
    this.setBuildingWhenUndefined(buildingId, currentBuilding, currentSiteData);
    this.setLevelWhenUndefined(currentLevel, currentSiteData);
    this.moveMapToBuilding(shouldMoveMap);
  }

  static handleBuildingAndLevelSetting(
    buildingId,
    levelId,
    currentBuilding,
    currentLevel,
    currentSiteData,
    shouldMoveMap
  ) {
    if (currentBuilding === undefined || buildingId === undefined) {
      this.handleBuildingAndLevelWhenUndefined(
        buildingId,
        currentBuilding,
        currentLevel,
        currentSiteData,
        shouldMoveMap
      );
    } else if (buildingId !== currentBuilding) {
      store.dispatch("MAP/SET_CURRENT_BUILDING", { building: buildingId });
    }

    if (levelId !== undefined) {
      store.dispatch("MAP/SET_CURRENT_LEVEL", { level: levelId });
    } else if (currentLevel !== undefined) {
      store.dispatch("MAP/SET_CURRENT_LEVEL", { level: currentLevel });
    }
  }

  static async initalizeSiteMapData(
    clientId,
    siteId,
    buildingId,
    levelId,
    shouldSetBuildingAndLevel = true,
    shouldMoveMap = true
  ) {
    const currentSiteData = store.state.CONTENT.currentSiteData;
    const currentBuilding = store.state.MAP.currentBuilding;
    const currentLevel = store.state.MAP.currentLevel;
    const isForceUpdate = this.shouldForceUpdate(siteId, currentSiteData);

    await store.dispatch("CONTENT/SET_CURRENT_SITE_DATA", {
      clientId: clientId,
      siteId: siteId,
      forceUpdate: isForceUpdate
    });

    if (shouldSetBuildingAndLevel) {
      this.handleBuildingAndLevelSetting(
        buildingId,
        levelId,
        currentBuilding,
        currentLevel,
        currentSiteData,
        shouldMoveMap
      );
    } else {
      const currentSiteObject = store.getters["MAP/currentSiteObject"];
      this.fitBoundsIfNotOnTheScreen(currentSiteObject?.geometry?.coordinates?.[0]);
    }

    await store.dispatch("CONTENT/SET_SITE_MAP_OBJECTS", {
      clientId: clientId,
      siteId: siteId,
      forceUpdate: isForceUpdate
    });
  }

  static enableMapInteractions(options) {
    let map;
    if (options?.map) {
      map = options.map;
    } else {
      map = store.getters["MAP/map"];
    }
    if (!map) {
      return;
    }

    map.touchZoomRotate.disable();
    map.dragRotate.disable();
    map["keyboard"].disable();
  }

  static disableMapInteractions(options) {
    let map;
    if (options?.map) {
      map = options.map;
    } else {
      map = store.getters["MAP/map"];
    }
    if (!map) {
      console.warn("Cannot disable map interactions - map is not initialized yet");
      return;
    }

    map.dragRotate.enable();
    map.touchZoomRotate.enable();
    map["keyboard"].enable();
  }

  static disableTilt() {
    const map = store.getters["MAP/map"];
    if (!map) {
      return;
    }
    // Disable pitch (tilt) while keeping rotation enabled
    map.setMaxPitch(0);
    map.setPitch(0);
  }

  static enableTilt() {
    const map = store.getters["MAP/map"];
    if (!map) {
      return;
    }
    // Restore default pitch controls (typically 85 degrees max)
    map.setMaxPitch(60);
    map.dragRotate.enable();
  }

  static toggleFeatureHighlight(map, hoveredStateId, hovered) {
    if (hoveredStateId !== undefined) {
      map.setFeatureState({ source: MapConstants.SOURCE, id: hoveredStateId }, { hover: hovered });
    }
  }

  static setMultipleHighlightState(map, featureIds, isHighlighted) {
    featureIds.forEach((featureId) => {
      map.setFeatureState({ source: MapConstants.SOURCE, id: featureId }, { hover: isHighlighted });
    });
  }

  static highlightContent(map, mapObjects, hoveredStateId, featureId) {
    let hoveredFeature = mapObjects.find((feature) => {
      return feature?.properties?.fid === featureId;
    });
    if (hoveredFeature) {
      if (hoveredStateId !== undefined) {
        this.toggleFeatureHighlight(map, hoveredStateId, false);
        hoveredStateId = undefined;
      }
      hoveredStateId = hoveredFeature?.id;
      this.toggleFeatureHighlight(map, hoveredStateId, true);
      return hoveredStateId;
    } else {
      this.toggleFeatureHighlight(map, hoveredStateId, false);
      return undefined;
    }
  }

  static unhighlightContent(map, hoveredStateId, hovered) {
    this.toggleFeatureHighlight(map, hoveredStateId, hovered);
  }

  static addAnchorDashSourceAndLayer() {
    const map = store.getters["MAP/map"];
    const dashedSource = {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: []
      }
    };
    if (!map?.getSource("anchors-dashed-line-source")) {
      map.addSource("anchors-dashed-line-source", dashedSource);
    }
    const dashedLineLayer = {
      id: "anchors-dashed-line",
      type: "line",
      source: "anchors-dashed-line-source",
      layout: {
        "line-join": "round",
        "line-cap": "round"
      },
      paint: {
        "line-color": "#346df1",
        "line-width": 4,
        "line-dasharray": [1, 2]
      }
    };
    if (!map.getLayer("anchors-dashed-line")) {
      map.addLayer(dashedLineLayer);
    }
  }

  static removeAnchorDashSourceAndLayer() {
    const map = store.getters["MAP/map"];
    if (!map) {
      return;
    }

    if (map?.getSource("anchors-dashed-line-source")) {
      map.removeSource("anchors-dashed-line-source");
    }
  }

  static validateInputs(pointCoords, buildings) {
    return pointCoords && buildings && Array.isArray(buildings) && buildings.length > 0;
  }

  static validatePointCoordinates(pointCoords) {
    return (
      Array.isArray(pointCoords) &&
      pointCoords.length === 2 &&
      typeof pointCoords[0] === "number" &&
      typeof pointCoords[1] === "number"
    );
  }

  static createPointFeature(pointCoords) {
    try {
      return point(pointCoords);
    } catch (error) {
      console.debug("Error creating point feature:", error, pointCoords);
      return null;
    }
  }

  static checkPointInPolygon(pointFeature, polygonCoords) {
    try {
      const buildingPolygon = polygon(polygonCoords);
      return booleanPointInPolygon(pointFeature, buildingPolygon);
    } catch (error) {
      return false;
    }
  }

  static checkPointInMultiPolygon(pointFeature, multiPolygonCoords) {
    for (const polygonCoords of multiPolygonCoords) {
      if (this.checkPointInPolygon(pointFeature, polygonCoords)) {
        return true;
      }
    }
    return false;
  }

  static isPointInBuilding(pointFeature, building) {
    if (!building.geometry || !building.geometry.coordinates) {
      return false;
    }

    try {
      if (building.geometry.type === "MultiPolygon") {
        return this.checkPointInMultiPolygon(pointFeature, building.geometry.coordinates);
      } else {
        return this.checkPointInPolygon(pointFeature, building.geometry.coordinates);
      }
    } catch (error) {
      console.debug("Error checking if point is inside building:", error, building);
      return false;
    }
  }

  /**
   * Check if a point is inside any building and return the building if found
   * @param {Array} pointCoords - [longitude, latitude] coordinates
   * @param {Array} buildings - Array of building features
   * @returns {Object|null} - Building feature if point is inside, null otherwise
   */
  static getBuildingAtPoint(pointCoords, buildings) {
    if (!this.validateInputs(pointCoords, buildings)) {
      return null;
    }

    if (!this.validatePointCoordinates(pointCoords)) {
      console.debug("Invalid point coordinates provided:", pointCoords);
      return null;
    }

    const pointFeature = this.createPointFeature(pointCoords);
    if (!pointFeature) {
      return null;
    }

    const centralBuildings = buildings.filter((building) => this.isPointInBuilding(pointFeature, building));
    return centralBuildings;
  }

  static getCurrentZoom() {
    const map = store.getters["MAP/map"];
    if (!map) {
      return undefined;
    }
    return map.getZoom();
  }

  static getGeoJSONType(coords) {
    const getDepth = (arr) => (Array.isArray(arr) ? 1 + getDepth(arr[0]) : 0);

    const depth = getDepth(coords);

    if (depth === 4) {
      return "MultiPolygon";
    }
    return "Polygon";
  }

  /**
   * Returns [lng, lat] center of a Point, Polygon, or MultiPolygon geometry.
   */
  static getGeometryCenter(geometry) {
    if (!geometry) return null;
    if (geometry.type === "Point" && geometry.coordinates?.length >= 2) {
      return geometry.coordinates;
    }
    if (geometry.type === "Polygon" || geometry.type === "MultiPolygon") {
      try {
        return center(geometry).geometry.coordinates;
      } catch (e) {
        console.error("Failed to get geometry center: ", e);
        return null;
      }
    }
    return null;
  }

  static moveGeometryToCenter(geometry, targetCenter) {
    if (!geometry || targetCenter?.length !== 2) {
      return geometry;
    }
    if (geometry?.type !== "Polygon" && geometry?.type !== "MultiPolygon") {
      return geometry;
    }
    try {
      const currentCenter = center(geometry).geometry.coordinates;
      const deltaLng = targetCenter[0] - currentCenter[0];
      const deltaLat = targetCenter[1] - currentCenter[1];
      const roundTo8 = (n) => Number.parseFloat(Number(n).toFixed(8));
      const translated = structuredClone(geometry);
      const translateCoords = (coords) =>
        coords.map((entry) =>
          Array.isArray(entry[0])
            ? translateCoords(entry)
            : [roundTo8(entry[0] + deltaLng), roundTo8(entry[1] + deltaLat)]
        );
      translated.coordinates = translateCoords(translated.coordinates);
      return translated;
    } catch (e) {
      console.error("Failed to move geometry center", e);
      return geometry;
    }
  }
}
